import React, { useState } from 'react';
import { episodes } from '../data/episodes';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiX, FiChevronRight, FiChevronLeft, FiCalendar } = FiIcons;

const Episodes = () => {
  const [selectedEpisode, setSelectedEpisode] = useState(null);

  // Helper: Open the modal
  const openEpisode = (episode) => {
    setSelectedEpisode(episode);
  };

  // Helper: Close modal
  const closeEpisode = () => {
    setSelectedEpisode(null);
  };

  // Helper: Go to Next/Prev
  const navigateEpisode = (direction) => {
    if (!selectedEpisode) return;
    
    const currentIndex = episodes.findIndex(e => e.id === selectedEpisode.id);
    // "Next" implies going back in time to the next episode in the series (higher index in our newest-first list)
    // "Previous" implies going forward to a newer episode (lower index)
    
    // Note: The list is sorted Newest (index 0) to Oldest.
    // Logic:
    // 'next' param -> we want the previous episode in the LIST (newer date) ? 
    // Wait, let's align with the button labels:
    // Button "Previous" (chronologically older) calls navigateEpisode('next') -> index + 1
    // Button "Next Episode" (chronologically newer/next in series) calls navigateEpisode('prev') -> index - 1
    
    let newIndex;
    
    if (direction === 'next') {
        newIndex = currentIndex + 1; 
    } else {
        newIndex = currentIndex - 1; 
    }

    if (newIndex >= 0 && newIndex < episodes.length) {
      setSelectedEpisode(episodes[newIndex]);
    }
  };

  return (
    <div className="bg-brand-cream min-h-screen py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-16 relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-1 bg-brand-gold rounded-full opacity-50"></div>
          <h1 className="mt-8 text-4xl md:text-5xl font-serif text-brand-green mb-6 tracking-tight">Video Blog & Episodes</h1>
          <p className="text-xl text-brand-olive max-w-2xl mx-auto leading-relaxed">
            Watch the latest stories of connection, healing, and discovery.
          </p>
        </div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {episodes.map((episode) => (
            <div key={episode.id} className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full group border border-brand-wheat/20">
              
              {/* Thumbnail Container */}
              <div 
                className="relative aspect-video bg-brand-wheat/20 cursor-pointer overflow-hidden"
                onClick={() => openEpisode(episode)}
              >
                <img 
                  src={`https://img.youtube.com/vi/${episode.youtubeId}/hqdefault.jpg`} 
                  alt={episode.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = 'https://images.unsplash.com/photo-1478737270239-2f02b77ac6d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80';
                  }}
                />
                <div className="absolute inset-0 bg-brand-green/20 group-hover:bg-brand-green/10 transition-colors flex items-center justify-center">
                  <div className="w-14 h-14 bg-white/95 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <SafeIcon icon={FiPlay} className="w-6 h-6 text-brand-green ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-3 right-3 bg-brand-green/90 text-white text-xs font-bold px-2 py-1 rounded shadow-sm">
                   {episode.duration}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center text-xs font-medium text-brand-olive mb-3 space-x-4 uppercase tracking-wider">
                  <span className="flex items-center">
                    <SafeIcon icon={FiCalendar} className="mr-1.5 w-4 h-4"/> 
                    {episode.date}
                  </span>
                </div>
                
                <h3 
                  className="text-xl font-serif font-bold text-brand-green mb-3 line-clamp-2 group-hover:text-brand-gold transition-colors cursor-pointer" 
                  onClick={() => openEpisode(episode)}
                >
                  {episode.title}
                </h3>
                
                <p className="text-gray-600 text-sm mb-6 line-clamp-3 flex-1 leading-relaxed">
                  {episode.excerpt}
                </p>

                <button 
                  onClick={() => openEpisode(episode)}
                  className="mt-auto w-full py-3 px-4 bg-brand-cream hover:bg-brand-wheat/30 text-brand-green border border-brand-wheat/30 rounded-lg text-sm font-bold transition-all duration-300 flex items-center justify-center uppercase tracking-wide"
                >
                  <SafeIcon icon={FiPlay} className="mr-2 w-4 h-4" /> Watch Episode
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* THE MODAL PLAYER (Cinema Mode) */}
      {selectedEpisode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-green/95 backdrop-blur-sm" onClick={closeEpisode}>
          
          <div className="relative w-full max-w-5xl bg-black rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh] ring-1 ring-white/10" onClick={e => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#1a1a1a]">
              <h2 className="text-brand-wheat font-medium truncate pr-4 font-serif">{selectedEpisode.title}</h2>
              <button 
                onClick={closeEpisode} 
                className="p-2 hover:bg-white/10 rounded-full text-gray-400 hover:text-white transition-colors"
                aria-label="Close modal"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            {/* Main Player Area (Scrollable if article is long) */}
            <div className="overflow-y-auto custom-scrollbar bg-[#111]">
              
              {/* YouTube Iframe */}
              <div className="aspect-video w-full bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedEpisode.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                  title={selectedEpisode.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>

              {/* Controls & Article */}
              <div className="p-6 md:p-10 bg-[#111]">
                
                {/* Nav Buttons */}
                <div className="flex items-center justify-between mb-10 pb-6 border-b border-white/10">
                  <button 
                    onClick={() => navigateEpisode('next')}
                    disabled={episodes.findIndex(e => e.id === selectedEpisode.id) === episodes.length - 1}
                    className="group flex items-center text-gray-400 hover:text-brand-gold disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-medium text-sm uppercase tracking-wider"
                  >
                    <SafeIcon icon={FiChevronLeft} className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" /> Previous
                  </button>

                  <button 
                    onClick={() => navigateEpisode('prev')}
                    disabled={episodes.findIndex(e => e.id === selectedEpisode.id) === 0}
                    className="group flex items-center text-gray-400 hover:text-brand-gold disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-medium text-sm uppercase tracking-wider"
                  >
                    Next Episode <SafeIcon icon={FiChevronRight} className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>

                {/* Article Content */}
                <div className="prose prose-invert prose-lg max-w-none">
                   <h3 className="text-brand-wheat text-xl font-serif mb-4">About this Episode</h3>
                   <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                     {selectedEpisode.seoArticle || "Detailed notes and transcript summary coming soon."}
                   </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default Episodes;